import { RefObject, useCallback, useLayoutEffect, useState } from 'react';
import { pick } from 'ramda';

import useResize from './useResize';

export interface Rect {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
}

type ComputeRectFn = () => void;

const getRect = (element: Element | null): Rect => {
  if (!element) {
    return {
      bottom: 0,
      height: 0,
      left: 0,
      right: 0,
      top: 0,
      width: 0,
    };
  }

  const rect = element.getBoundingClientRect();

  return pick(['top', 'right', 'bottom', 'left', 'width', 'height'], rect);
};

const useRect = (elementRef: RefObject<Element>): [Rect, ComputeRectFn] => {
  const [rect, setRect] = useState(getRect(elementRef.current || null));

  const computeRect = useCallback((): void => {
    if (elementRef.current) {
      setRect(getRect(elementRef.current));
    }
  }, [elementRef]);

  useResize(computeRect);

  // Compute the initial rect
  useLayoutEffect(() => {
    computeRect();
  }, [computeRect]);

  return [rect, computeRect];
};

export default useRect;
